import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import SupervisorAccountOutlinedIcon from "@mui/icons-material/SupervisorAccountOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Button, ButtonGroup, Card, CardContent, Chip, Dialog, DialogContent, IconButton, InputAdornment, Menu, MenuItem, TextField, Tooltip } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { financeApi } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { useBlockedAccessGuard } from "../hooks/useBlockedAccessGuard";
import { memberAvatarColor, memberInitials, userAvatarColor, userInitials } from "../lib/avatar";
import type { FamilyInvite, Group, Member, User } from "../lib/types";
import { queryKeys } from "../lib/queryKeys";

type UsersGroupPageProps = {
  group: Group;
  user: User;
  onBlockedAccess?: () => void;
};

type RoleFilter = "all" | "admin" | "member";

const permissionRows = [
  { label: "View balances", owner: true, admin: true, member: true },
  { label: "Create records", owner: true, admin: true, member: true },
  { label: "Edit shared categories", owner: true, admin: true, member: false },
  { label: "Manage users", owner: true, admin: true, member: false },
  { label: "Delete group", owner: true, admin: false, member: false },
];

const roleOptions: RoleFilter[] = ["all", "admin", "member"];
const avatarSize = 256;
const maxAvatarBytes = 120_000;

function roleLabel(role: string) {
  return role.slice(0, 1).toUpperCase() + role.slice(1);
}

function inviteUrl(invite: FamilyInvite) {
  if (typeof window === "undefined") return invite.url;
  return new URL(invite.url, window.location.origin).toString();
}

function expiresLabel(expiresAt: string) {
  const expires = new Date(expiresAt);
  if (Number.isNaN(expires.getTime())) return "Expiration unknown";
  const diffMs = expires.getTime() - Date.now();
  if (diffMs <= 0) return "Expired";
  const days = Math.ceil(diffMs / 86_400_000);
  if (days <= 1) return "Expires today";
  return `Expires in ${days} days`;
}

function inviteStatusLabel(invite: FamilyInvite) {
  if (invite.is_expired) {
    return `Expired · ${invite.used_count} ${invite.used_count === 1 ? "activation" : "activations"} used`;
  }
  return `${expiresLabel(invite.expires_at)} · ${invite.used_count} of ${invite.max_uses} used`;
}

function groupInitials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "G";
}

function loadImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image"));
    };
    image.src = url;
  });
}

async function compressAvatarFile(file: File) {
  if (!file.type.startsWith("image/")) {
    throw new Error("Choose an image file");
  }

  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = avatarSize;
  canvas.height = avatarSize;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Could not prepare avatar");

  const sourceSize = Math.min(image.naturalWidth, image.naturalHeight);
  const sourceX = (image.naturalWidth - sourceSize) / 2;
  const sourceY = (image.naturalHeight - sourceSize) / 2;
  context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, avatarSize, avatarSize);

  for (const quality of [0.86, 0.76, 0.66]) {
    const dataUrl = canvas.toDataURL("image/jpeg", quality);
    if (dataUrl.length <= maxAvatarBytes) return dataUrl;
  }
  return canvas.toDataURL("image/jpeg", 0.58);
}

function UsersGroupMember({
  member,
  isCurrentUser,
  onChangeRole,
  onToggleBlocked,
  onDelete,
}: {
  member: Member;
  isCurrentUser: boolean;
  onChangeRole: (member: Member) => void;
  onToggleBlocked: (member: Member) => void;
  onDelete: (member: Member) => void;
}) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);
  const isBlocked = member.is_blocked;

  return (
    <article className="users-group-member">
      <div className="users-group-member-main">
        <span className={`users-group-avatar${member.avatar_data_url ? " has-image" : ""}`} style={{ backgroundColor: member.avatar_data_url ? undefined : memberAvatarColor(member) }}>
          {member.avatar_data_url ? <img src={member.avatar_data_url} alt="" /> : memberInitials(member)}
        </span>
        <div className="users-group-member-copy">
          <div className="users-group-member-name">
            <strong>{member.display_name}</strong>
            {isCurrentUser ? <Chip className="users-group-self-chip" label="It's you" size="small" /> : null}
          </div>
          <span>{member.email}</span>
          <small>{member.invited_by_display_name ? `Invited by ${member.invited_by_display_name}` : "Initial group member"}</small>
        </div>
      </div>
      <Chip className={`users-group-role ${member.role}`} label={roleLabel(member.role)} size="small" />
      <span className={`users-group-status ${isBlocked ? "blocked" : "active"}`}>{isBlocked ? "Blocked" : "Active"}</span>
      {isCurrentUser ? null : (
        <IconButton
          className="records-icon-button users-group-action"
          type="button"
          aria-label={`More actions for ${member.display_name}`}
          aria-controls={menuOpen ? `member-menu-${member.user_id}` : undefined}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title="More actions"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          <MoreHorizIcon aria-hidden="true" />
        </IconButton>
      )}
      <Menu
        id={`member-menu-${member.user_id}`}
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { className: "users-group-menu" } }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onChangeRole(member);
          }}
          disabled={member.role === "owner"}
        >
          <SupervisorAccountOutlinedIcon aria-hidden="true" /> Change role
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onToggleBlocked(member);
          }}
          disabled={member.role === "owner"}
        >
          {isBlocked ? <LockOpenOutlinedIcon aria-hidden="true" /> : <LockOutlinedIcon aria-hidden="true" />}
          {isBlocked ? "Unblock user" : "Block user"}
        </MenuItem>
        <MenuItem
          className="danger"
          onClick={() => {
            setMenuAnchor(null);
            onDelete(member);
          }}
          disabled={member.role === "owner"}
        >
          <DeleteOutlineIcon aria-hidden="true" /> Delete user
        </MenuItem>
      </Menu>
    </article>
  );
}

function InviteUserModal({
  group,
  invites,
  open,
  onClose,
}: {
  group: Group;
  invites: FamilyInvite[];
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState("member");
  const [maxUses, setMaxUses] = useState(1);
  const [status, setStatus] = useState("");
  const [statusKind, setStatusKind] = useState<"error" | "success">("error");

  const invalidateInvites = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.invites(group.id) });
  };

  const createMutation = useMutation({
    mutationFn: () => financeApi.createInvite(group.id, { role, max_uses: maxUses }),
    onSuccess: async () => {
      setStatus("");
      setStatusKind("error");
      setMaxUses(1);
      await invalidateInvites();
    },
    onError: (error) => {
      setStatusKind("error");
      setStatus(error instanceof Error ? error.message : "Could not create invite");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (inviteId: string) => financeApi.deleteInvite(group.id, inviteId),
    onSuccess: invalidateInvites,
    onError: (error) => {
      setStatusKind("error");
      setStatus(error instanceof Error ? error.message : "Could not delete invite");
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    createMutation.mutate();
  };

  const copyInvite = async (invite: FamilyInvite) => {
    const copied = await copyText(inviteUrl(invite));
    setStatusKind(copied ? "success" : "error");
    setStatus(copied ? "Invite link copied." : "Could not copy invite link.");
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="inviteUserTitle" slotProps={{ paper: { className: "modal records-modal users-group-modal" } }}>
      <DialogContent className="records-modal-content">
        <div className="records-modal-toolbar">
          <div>
            <h2 id="inviteUserTitle">Invite user</h2>
            <p className="users-group-modal-subtitle">Create and manage invite links for this group.</p>
          </div>
          <IconButton className="records-icon-button" type="button" aria-label="Close invite dialog" title="Close" onClick={onClose}>
            <CloseIcon aria-hidden="true" />
          </IconButton>
        </div>

        <form className="users-group-invite-create" onSubmit={submit}>
          <TextField label="Invite label" defaultValue="Group invite" />
          <TextField select label="Role" value={role} slotProps={{ select: { native: true } }} onChange={(event) => setRole(event.target.value)}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </TextField>
          <TextField
            label="Activations"
            value={maxUses}
            type="number"
            slotProps={{ htmlInput: { min: 1, max: 10 } }}
            onChange={(event) => setMaxUses(Math.min(10, Math.max(1, Number(event.target.value) || 1)))}
          />
          <Button variant="contained" startIcon={<AddIcon />} type="submit" disabled={createMutation.isPending}>
            Create link
          </Button>
        </form>
        {status ? <p className={statusKind === "success" ? "records-status" : "records-error"}>{status}</p> : null}

        <div className="users-group-invite-list">
          {invites.length ? (
            invites.map((invite) => (
              <article className="users-group-invite-row" key={invite.id}>
                <div>
                  <strong>{roleLabel(invite.role)} invite</strong>
                  <code>{inviteUrl(invite)}</code>
                  <span>
                    {invite.created_by_display_name} · {inviteStatusLabel(invite)}
                  </span>
                </div>
                <div className="users-group-invite-actions">
                  <Button variant="outlined" startIcon={<ContentCopyOutlinedIcon />} type="button" onClick={() => void copyInvite(invite)}>
                    Copy invite
                  </Button>
                  <IconButton
                    className="records-icon-button danger"
                    type="button"
                    aria-label={`Delete invite ${invite.code}`}
                    title="Delete invite"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(invite.id)}
                  >
                    <DeleteOutlineIcon aria-hidden="true" />
                  </IconButton>
                </div>
              </article>
            ))
          ) : (
            <p className="users-group-empty">No invite links yet.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditGroupModal({ group, open, onClose }: { group: Group; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(group.name);
  const [baseCurrency, setBaseCurrency] = useState(group.base_currency);
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(group.avatar_data_url || null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(group.name);
    setBaseCurrency(group.base_currency);
    setAvatarDataUrl(group.avatar_data_url || null);
    setStatus("");
  }, [group, open]);

  const updateMutation = useMutation({
    mutationFn: () => financeApi.updateGroup(group.id, { name: name.trim(), avatar_data_url: avatarDataUrl, base_currency: baseCurrency.trim().toUpperCase() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.groups });
      onClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Could not update group"),
  });

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setStatus("");
      setAvatarDataUrl(await compressAvatarFile(file));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not process image");
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim()) {
      setStatus("Group name is required");
      return;
    }
    updateMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="editGroupTitle" slotProps={{ paper: { className: "modal records-modal users-group-modal" } }}>
      <DialogContent>
        <form className="records-modal-content" onSubmit={submit}>
          <div className="records-modal-toolbar">
            <div>
              <h2 id="editGroupTitle">Edit group</h2>
              <p className="users-group-modal-subtitle">Update the group avatar and display name.</p>
            </div>
            <IconButton className="records-icon-button" type="button" aria-label="Close group dialog" title="Close" onClick={onClose}>
              <CloseIcon aria-hidden="true" />
            </IconButton>
          </div>

          <div className="users-group-edit-form">
            <div className="users-group-avatar-editor">
              <span className={`users-group-avatar-preview${avatarDataUrl ? " has-image" : ""}`}>
                {avatarDataUrl ? <img src={avatarDataUrl} alt="" /> : groupInitials(name)}
              </span>
              <div>
                <strong>Group avatar</strong>
                <span className="users-group-avatar-hint">Images are cropped to the center and compressed for the avatar circle.</span>
                <div className="users-group-avatar-actions">
                  <Button variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />} type="button">
                    Upload image
                    <input hidden accept="image/png,image/jpeg,image/webp" type="file" onChange={handleAvatarChange} />
                  </Button>
                  {avatarDataUrl ? (
                    <Button variant="text" color="error" type="button" onClick={() => setAvatarDataUrl(null)}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            <TextField label="Group name" value={name} onChange={(event) => setName(event.target.value)} required />
            <TextField
              label="Base currency"
              value={baseCurrency}
              slotProps={{ htmlInput: { maxLength: 3 } }}
              helperText="Can be changed until the group has its first record."
              onChange={(event) => setBaseCurrency(event.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3))}
              required
            />
          </div>
          {status ? <p className="records-error">{status}</p> : null}

          <div className="records-modal-actions">
            <Button variant="outlined" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={updateMutation.isPending}>
              Save changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditProfileModal({ user, open, onClose }: { user: User; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(user.display_name);
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(user.avatar_data_url || null);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!open) return;
    setDisplayName(user.display_name);
    setAvatarDataUrl(user.avatar_data_url || null);
    setPassword("");
    setPasswordConfirm("");
    setShowPassword(false);
    setShowPasswordConfirm(false);
    setStatus("");
  }, [user, open]);

  const updateMutation = useMutation({
    mutationFn: () =>
      financeApi.updateMe({
        display_name: displayName.trim(),
        avatar_data_url: avatarDataUrl,
        ...(password ? { password } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      await queryClient.invalidateQueries({ queryKey: ["members"] });
      onClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Could not update profile"),
  });

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      setStatus("");
      setAvatarDataUrl(await compressAvatarFile(file));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Could not process image");
    }
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!displayName.trim()) {
      setStatus("Name is required");
      return;
    }
    if (password && password.length < 8) {
      setStatus("Password must be at least 8 characters");
      return;
    }
    if (password !== passwordConfirm) {
      setStatus("Passwords do not match");
      return;
    }
    updateMutation.mutate();
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="editProfileTitle" slotProps={{ paper: { className: "modal records-modal users-group-modal" } }}>
      <DialogContent>
        <form className="records-modal-content" onSubmit={submit}>
          <div className="records-modal-toolbar">
            <div>
              <h2 id="editProfileTitle">Edit profile</h2>
              <p className="users-group-modal-subtitle">Update your avatar, name, or password.</p>
            </div>
            <IconButton className="records-icon-button" type="button" aria-label="Close profile dialog" title="Close" onClick={onClose}>
              <CloseIcon aria-hidden="true" />
            </IconButton>
          </div>

          <div className="users-group-edit-form">
            <div className="users-group-avatar-editor">
              <span className={`users-group-avatar-preview${avatarDataUrl ? " has-image" : ""}`} style={{ backgroundColor: avatarDataUrl ? undefined : userAvatarColor(user) }}>
                {avatarDataUrl ? <img src={avatarDataUrl} alt="" /> : userInitials({ ...user, display_name: displayName })}
              </span>
              <div>
                <strong>Your avatar</strong>
                <span className="users-group-avatar-hint">Images are cropped to the center and compressed for the avatar circle.</span>
                <div className="users-group-avatar-actions">
                  <Button variant="outlined" component="label" startIcon={<UploadFileOutlinedIcon />} type="button">
                    Upload image
                    <input hidden accept="image/png,image/jpeg,image/webp" type="file" onChange={handleAvatarChange} />
                  </Button>
                  {avatarDataUrl ? (
                    <Button variant="text" color="error" type="button" onClick={() => setAvatarDataUrl(null)}>
                      Remove
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>

            <TextField label="Name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} required />
            <TextField label="Email" value={user.email} disabled />
            <TextField
              label="New password"
              type={showPassword ? "text" : "password"}
              value={password}
              helperText="Leave blank to keep current password."
              onChange={(event) => setPassword(event.target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        type="button"
                        aria-label={showPassword ? "Hide new password" : "Show new password"}
                        title={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword((current) => !current)}
                      >
                        {showPassword ? <VisibilityOffOutlinedIcon aria-hidden="true" /> : <VisibilityOutlinedIcon aria-hidden="true" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label="Confirm new password"
              type={showPasswordConfirm ? "text" : "password"}
              value={passwordConfirm}
              disabled={!password}
              onChange={(event) => setPasswordConfirm(event.target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        type="button"
                        aria-label={showPasswordConfirm ? "Hide password confirmation" : "Show password confirmation"}
                        title={showPasswordConfirm ? "Hide password" : "Show password"}
                        disabled={!password}
                        onClick={() => setShowPasswordConfirm((current) => !current)}
                      >
                        {showPasswordConfirm ? <VisibilityOffOutlinedIcon aria-hidden="true" /> : <VisibilityOutlinedIcon aria-hidden="true" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </div>
          {status ? <p className="records-error">{status}</p> : null}

          <div className="records-modal-actions">
            <Button variant="outlined" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={updateMutation.isPending}>
              Save profile
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ChangeRoleModal({ group, member, open, onClose }: { group: Group; member: Member | null; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [role, setRole] = useState("member");
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!open) return;
    setRole(member?.role === "admin" ? "admin" : "member");
    setStatus("");
  }, [member, open]);

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!member) throw new Error("Member is required");
      return financeApi.updateMember(group.id, member.user_id, { role });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.members(group.id) });
      onClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Could not update role"),
  });

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="changeRoleTitle" slotProps={{ paper: { className: "modal records-modal users-group-modal" } }}>
      <DialogContent className="records-modal-content">
        <div className="records-modal-toolbar">
          <div>
            <h2 id="changeRoleTitle">Change role</h2>
            <p className="users-group-modal-subtitle">
              {member ? `Set a new role for ${member.display_name} (${member.email}).` : "Set a new member role."}
            </p>
          </div>
          <IconButton className="records-icon-button" type="button" aria-label="Close role dialog" title="Close" onClick={onClose}>
            <CloseIcon aria-hidden="true" />
          </IconButton>
        </div>

        {member ? (
          <div className="users-group-role-target">
            <span className={`users-group-avatar${member.avatar_data_url ? " has-image" : ""}`} style={{ backgroundColor: member.avatar_data_url ? undefined : memberAvatarColor(member) }}>
              {member.avatar_data_url ? <img src={member.avatar_data_url} alt="" /> : memberInitials(member)}
            </span>
            <div>
              <strong>{member.display_name}</strong>
              <span>{member.email}</span>
            </div>
          </div>
        ) : null}

        <div className="users-group-edit-form">
          <TextField select label="Role" value={role} slotProps={{ select: { native: true } }} onChange={(event) => setRole(event.target.value)}>
            <option value="admin">Admin</option>
            <option value="member">Member</option>
          </TextField>
        </div>
        {status ? <p className="records-error">{status}</p> : null}

        <div className="records-modal-actions">
          <Button variant="outlined" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" type="button" disabled={!member || updateMutation.isPending} onClick={() => updateMutation.mutate()}>
            Save role
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function UsersGroupPage({ group, user, onBlockedAccess }: UsersGroupPageProps) {
  const queryClient = useQueryClient();
  const [activeRole, setActiveRole] = useState<RoleFilter>("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [roleMember, setRoleMember] = useState<Member | null>(null);
  const canManageUsers = group.role === "owner" || group.role === "admin";
  const membersQuery = useQuery({ queryKey: queryKeys.members(group.id), queryFn: () => financeApi.members(group.id), retry: false });
  const invitesQuery = useQuery({ queryKey: queryKeys.invites(group.id), queryFn: () => financeApi.invites(group.id), enabled: canManageUsers, retry: false });
  const members = useMemo(() => membersQuery.data || [], [membersQuery.data]);
  const invites = useMemo(() => invitesQuery.data || [], [invitesQuery.data]);
  const sortedMembers = useMemo(
    () =>
      [...members].sort((left, right) => {
        const rank = (member: Member) => {
          if (member.user_id === user.id) return 0;
          if (member.role === "owner") return 1;
          return 2;
        };
        const rankDelta = rank(left) - rank(right);
        if (rankDelta !== 0) return rankDelta;
        return left.display_name.localeCompare(right.display_name);
      }),
    [members, user.id],
  );
  const filteredMembers = useMemo(
    () => (activeRole === "all" ? sortedMembers : sortedMembers.filter((member) => member.role === activeRole)),
    [activeRole, sortedMembers],
  );

  useBlockedAccessGuard([membersQuery.error, invitesQuery.error], onBlockedAccess);

  const invalidateMembers = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.members(group.id) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.groups });
  };
  const toggleBlockedMutation = useMutation({
    mutationFn: (member: Member) => financeApi.updateMember(group.id, member.user_id, { is_blocked: !member.is_blocked }),
    onSuccess: invalidateMembers,
  });
  const deleteMemberMutation = useMutation({
    mutationFn: (member: Member) => financeApi.deleteMember(group.id, member.user_id),
    onSuccess: invalidateMembers,
  });
  const isOwner = group.role === "owner";
  const leaveGroupMutation = useMutation({
    mutationFn: () => financeApi.deleteMember(group.id, user.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
    onError: (error) => window.alert(error instanceof Error ? error.message : "Could not leave group"),
  });

  return (
    <>
      <header className="topbar">
        <div>
          <h1>Users and group</h1>
          <p>{group.name} access, roles, invites, and shared permissions.</p>
        </div>
        <Chip className="chip" label="Live data" size="small" />
      </header>

      <section className="content users-group-content">
        <div className="users-group-hero">
          <div className="users-group-title">
            <span className="users-group-title-icon">
              {group.avatar_data_url ? <img src={group.avatar_data_url} alt="" /> : <GroupOutlinedIcon aria-hidden="true" />}
            </span>
            <div>
              <h2>{group.name}</h2>
              <div className="users-group-title-meta">
                <span>Base currency {group.base_currency}</span>
                <Chip className={`users-group-role ${group.role}`} label={roleLabel(group.role)} size="small" />
              </div>
            </div>
          </div>
          <div className="users-group-actions">
            <Button variant="contained" startIcon={<EditOutlinedIcon />} type="button" onClick={() => setEditGroupOpen(true)}>
              Edit group
            </Button>
            <Tooltip title={isOwner ? "The group owner can't leave the group" : "Leave group"}>
              <span>
                <Button
                  className="users-group-leave-button"
                  variant="contained"
                  type="button"
                  aria-label="Leave group"
                  disabled={isOwner || leaveGroupMutation.isPending}
                  onClick={() => leaveGroupMutation.mutate()}
                >
                  <LogoutOutlinedIcon aria-hidden="true" />
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>

        <Card className="users-group-panel users-group-members-panel">
          <CardContent>
            <div className="users-group-panel-header">
              <div>
                <h2>Members</h2>
              </div>
              <div className="users-group-members-controls">
                {canManageUsers ? (
                  <Button variant="outlined" startIcon={<PersonAddAlt1OutlinedIcon />} type="button" onClick={() => setInviteOpen(true)}>
                    Invite user
                  </Button>
                ) : null}
                <ButtonGroup variant="outlined" aria-label="Filter members by role">
                  {roleOptions.map((role) => (
                    <Button key={role} type="button" variant={activeRole === role ? "contained" : "outlined"} onClick={() => setActiveRole(role)}>
                      {roleLabel(role)}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
            </div>
            <div className="users-group-member-list">
              {filteredMembers.map((member) => (
                <UsersGroupMember
                  key={member.user_id}
                  member={member}
                  isCurrentUser={member.user_id === user.id}
                  onChangeRole={setRoleMember}
                  onToggleBlocked={(target) => toggleBlockedMutation.mutate(target)}
                  onDelete={(target) => deleteMemberMutation.mutate(target)}
                />
              ))}
              {!membersQuery.isLoading && !filteredMembers.length ? <p className="users-group-empty">No members found.</p> : null}
              {membersQuery.isError ? <p className="records-error">Could not load members.</p> : null}
            </div>
          </CardContent>
        </Card>

        <Card className="users-group-panel">
          <CardContent>
            <div className="users-group-panel-header">
              <div>
                <h2>Permission matrix</h2>
                <p>Default access by role.</p>
              </div>
            </div>
            <div className="users-group-permissions" role="table" aria-label="Permission matrix">
              <div className="users-group-permission-row header" role="row">
                <span role="columnheader">Permission</span>
                <span role="columnheader">Owner</span>
                <span role="columnheader">Admin</span>
                <span role="columnheader">Member</span>
              </div>
              {permissionRows.map((row) => (
                <div className="users-group-permission-row" role="row" key={row.label}>
                  <strong role="cell">{row.label}</strong>
                  {[row.owner, row.admin, row.member].map((allowed, index) => (
                    <span role="cell" className={allowed ? "allowed" : "blocked"} key={`${row.label}-${index}`}>
                      {allowed ? <CheckCircleOutlineIcon aria-label="Allowed" /> : "No"}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      <InviteUserModal group={group} invites={invites} open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <EditGroupModal group={group} open={editGroupOpen} onClose={() => setEditGroupOpen(false)} />
      <ChangeRoleModal group={group} member={roleMember} open={Boolean(roleMember)} onClose={() => setRoleMember(null)} />
    </>
  );
}
